import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { BiEdit } from 'react-icons/bi';
import { FaSitemap } from "react-icons/fa";
import { FaCheckToSlot, FaUserLarge, FaUsersGear } from 'react-icons/fa6';
import { MdOutlineExpandLess, MdOutlineExpandMore } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { LabelConstants, rolesByDimensionItems } from '../../Constants.ts';
import CreateButton from '../../UIComponents/CreateButton.tsx';
import DeleteRowButton from '../../UIComponents/DeleteRowButton.tsx';
import { AreObjectsEquals, getMyLocalTime, singularize } from '../../Utilities/Utilities.ts';
import { AuditObjectTypes, UserModel } from '../../_core/_model.ts';
import { DeleteRoleGroup } from '../../_core/_request.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import ComponentLayout from '../Layouts/ComponentLayout.tsx';
import SearchAndAdvancedFilters, { appliedFilterType } from '../Tables/SearchAndAdvancedFilters.tsx';
import TableLayoutEx from '../Tables/TableLayoutEx.tsx';
import TableSummary from '../Tables/TableSummary.tsx';
import UserWithDateCell from '../Tables/UserWithDateCell.tsx';
import TooltipButton from '../Tools/TooltipButton.tsx';
import GroupExpandView from './GroupExpandView.tsx';
import { RoleGroup, RoleGroupRequestParam } from './_core/_model.ts';
import { GetRoleGroups } from './_core/_request.ts';
import { useToast } from '../../context/ToastContext.tsx';


const roleItems = Object.values(rolesByDimensionItems).flat();  // Flatten all roles across all dimensions

const totalWeight = 32;

const minColumn = [
    {
        accessorKey: 'roleGroupName', header: 'Group Name',
        cell: ({ cell, row }) => {
            return (<div>
                {cell.getValue()}
                {/* <AccessIndicator
          score={row.original.roles.reduce((acc, role) => acc + (row.original.weight || 0), 0)} max={totalWeight} needLabel={false} /> */}
            </div>)
        }
    }
];

export default function RoleGroupsManager() {
    const [groups, setGroups] = useState<RoleGroup[] | null>(null);
    const { allRoleGroups, setAllRoleGroups, allUsers, roles } = useAppContext();
    const { user } = useContext(AuthContext);
    const defaultFilter = { roleGroupID: null, roleGroupName: "", roleID: null, userID: null };
    const [selectedFilter, setSelectedFilter] = useState<RoleGroupRequestParam>(defaultFilter);
    const { showToast } = useToast();
    //const [selectedGroup, setSelectedGroup] = React.useState<RoleGroup | null>(null);
    const navigate = useNavigate();

    const setSelectedGroup = (g: RoleGroup) => {
        navigate("edit?id=" + g.roleGroupID);
    }

    const getRows = (request: RoleGroupRequestParam, afterLoad: any) => {
        GetRoleGroups(request).then((x) => {
            setSelectedFilter(request);
            setGroups([...x.data]);
            if (typeof afterLoad != 'undefined')
                afterLoad(x.data);
        })
    }

    const initGroups = () => {
        setGroups(null);
        getRows(selectedFilter, undefined)
    }

    useEffect(() => {
        initGroups();
    }, [])


    const columns = {
        "expander": {
            accessorKey: 'expander',
            header: () => null,
            size: 30,
            enableSorting: false,
            enableResizing: false,
            cell: ({ row }) => (
                <TooltipButton title={row.getIsExpanded() ? "Collapse" : "View Roles Associated with This Group"}>
                    <span onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}>
                        {row.getIsExpanded() ? <MdOutlineExpandLess size={24} /> : <MdOutlineExpandMore size={24} />}
                    </span>
                </TooltipButton>
            ),
            meta: {
                expandedUI: (row) => <GroupExpandView emptyUI={
                    <AnimatePresence>
                        <motion.div
                            className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                        >
                            <FaCheckToSlot size={60} color="#adb5bd" className="mb-3" />
                            <h5 className="fw-semibold">No Roles</h5>
                            <p className="text-center" style={{ maxWidth: 400 }}>
                                This {LabelConstants.RoleGroups} does not have any Roles.
                            </p>
                        </motion.div>
                    </AnimatePresence>
                } roleIDS={row.original.roleIDS} tClass="px-4 py-3" />,
            },
        },
        "roleGroupName": {
            accessorKey: 'roleGroupName', header: 'Group Name', enableSorting: true,
            enableResizing: true,
            cell: ({ cell, row }) => {
                return (<div>
                    {cell.getValue()}
                    {/* <AccessIndicator
              score={row.original.roles.reduce((acc, role) => acc + (row.original.weight || 0), 0)} max={totalWeight} needLabel={false} /> */}
                </div>)
            }
        },
        "roleGroupDescription": {
            accessorKey: 'roleGroupDescription', header: 'Description', enableSorting: true,
            enableResizing: true, cell: (cell) => cell.getValue()
        },
        "createdBy": {
            accessorKey: 'createdBy', size: 150, header: 'Created by', enableSorting: true,
            enableResizing: true, cell: ({ cell, row }) => {
                return <UserWithDateCell {...{
                    tDate: getMyLocalTime(row.original.createdAt),
                    userId: cell.getValue()
                }} />
            }
        },
        "action": {
            accessorKey: 'action', size: 80, header: '',
            enableSorting: false,
            enableResizing: false, cell: ({ cell, row }) => (
                <div className='d-flex flex-row align-items-center gap-1'>
                    <TooltipButton title={`Edit Group`}>
                        <button className='btn btn-outline-primary py-1 px-2 m-0 border-0' onClick={(e) => {
                            e.stopPropagation(); // Prevents row selection
                            setSelectedGroup(row.original)
                        }}><BiEdit size={20} /></button>
                    </TooltipButton>
                    <DeleteRowButton {...{
                        cell: cell, row: row, deleteAPI: (r) => {
                            DeleteRoleGroup(row.original.roleGroupID, user.userID).then((x) => {
                                if (x.data == "") {
                                    GetRoleGroups({ roleGroupID: null, roleGroupName: "", roleID: "", userID: "" }).then((g) => {
                                        setAllRoleGroups([...g.data]);
                                    })
                                } else {
                                    showToast({
                                        message: `Failed to Delete ${LabelConstants.RoleGroups} ${row.original.roleGroupName} with error: ${x.data}`,
                                        type: "warning",
                                        duration: 5000
                                    })
                                }
                            });
                        }, nameFieldId: "roleGroupName", title: singularize(LabelConstants.RoleGroups)
                    }} />
                </div>
            )
        }
    };

    return (
        <ComponentLayout exButtons={<div></div>} instanceId={0} objectType={AuditObjectTypes.RoleGroup}
            title={LabelConstants.RoleGroups}
            bodyHeight={300} subHeader={
                <div className='d-flex flex-column w-100'>
                    <SearchAndAdvancedFilters {...{
                        initialValues: { "": null },
                        exButton: <div className='d-flex flex-row align-items-center gap-2 j_g_create' >
                            <CreateButton {...{
                                title: `Click to create a new ${singularize(LabelConstants.RoleGroups).toLowerCase()}`,
                                label: `Create a ${singularize(LabelConstants.RoleGroups).toLowerCase()}`,
                                onClick: () => {
                                    setSelectedGroup({
                                        roleGroupID: 0,
                                        roleGroupName: "",
                                        roleGroupDescription: "",
                                        roleIDS: "",
                                        createdBy: 1,
                                        createdAt: null
                                    })
                                }
                            }} />
                        </div>,
                        onChange: (filters, afterLoad) => {
                            getRows({
                                roleID: filters.roleID, roleGroupName: filters.name,
                                roleGroupID: (typeof filters.roleGroupID != 'undefined') ? filters.roleGroupID : null, userID: filters.userID
                            }, afterLoad);
                        },
                        filters: {
                            "roleID": {
                                exValues: [],
                                filterType: appliedFilterType.Role,
                                icon: <FaUsersGear />,
                                label: "Role"
                            },
                            "userID": {
                                exValues: [],
                                filterType: appliedFilterType.User,
                                icon: <FaUserLarge />,
                                label: "Users"
                            }
                        },
                        values: {
                            "roleID": null,
                            "userID": null
                        },
                    }} />
                    <TableSummary {...{
                        total: allRoleGroups?.length, filterred: groups?.length, name: LabelConstants.RoleGroups,
                        applyiedFilter: !AreObjectsEquals(selectedFilter, defaultFilter)
                    }} />
                </div>
            } cBody={
                <TableLayoutEx columnDefinition={columns} expandedUI={(row) => <GroupExpandView emptyUI={
                    <AnimatePresence>
                        <motion.div
                            className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                        >
                            <FaCheckToSlot size={60} color="#adb5bd" className="mb-3" />
                            <h5 className="fw-semibold">No Roles</h5>
                            <p className="text-center" style={{ maxWidth: 400 }}>
                                This {LabelConstants.RoleGroups} does not have any Roles.
                            </p>
                        </motion.div>
                    </AnimatePresence>
                } roleIDS={row.original.roleIDS} tClass="px-4 py-3" />} needRowSelection={false}
                    objType={AuditObjectTypes.RoleGroup} rows={groups == null ? [] : groups} onRowSelection={undefined} handleRowDoubleClick={(row) => {
                        setSelectedGroup(row.original)
                    }} emptyMessage={
                        <AnimatePresence>
                            <motion.div
                                className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                style={{
                                    border: "2px dashed #dee2e6",
                                    borderRadius: "10px",
                                    marginTop: "40px",
                                }}
                            >
                                <FaSitemap size={60} color="#adb5bd" className="mb-3" />
                                <h5 className="fw-semibold">No {LabelConstants.RoleGroups} Found</h5>
                                {
                                    (!AreObjectsEquals(selectedFilter, defaultFilter)) ?
                                        <p className="text-center" style={{ maxWidth: 400 }}>
                                            No {LabelConstants.RoleGroups} found for selected filter.
                                        </p> :
                                        <p className="text-center" style={{ maxWidth: 400 }}>
                                            It looks like there are no records yet. Click{" "}
                                            <strong>“Create”</strong> to get started.
                                        </p>
                                }
                            </motion.div>
                        </AnimatePresence>} needSelection={false} loading={groups == null} isSelected={(row) => {
                            return false;
                        }} setSelected={(row) => {
                            //if (selectedGroup?.roleGroupID != row.original.roleGroupID)
                            //setSelectedGroup(row.original)
                            //else
                            //    setSelectedGroup(null)
                        }} />
            }></ComponentLayout >
    );
}
